import { Spin } from 'ant-design-vue';
import {
  AsyncComponentLoader,
  Component,
  ComponentInternalInstance,
  ComponentPublicInstance,
  defineAsyncComponent,
  defineComponent,
  h,
  onActivated,
  onMounted,
  shallowRef,
  // FunctionalComponent, CSSProperties
} from 'vue';
import { noop } from '..';

// const Loading: FunctionalComponent<{ size: 'small' | 'default' | 'large' }> = (props) => {
//   const style: CSSProperties = {
//     position: 'absolute',
//     display: 'flex',
//     justifyContent: 'center',
//     alignItems: 'center',
//   };
//   return (
//     <div style={style}>
//       <Spin spinning={true} size={props.size} />
//     </div>
//   );
// };

interface Options {
  size?: 'default' | 'small' | 'large';
  delay?: number;
  timeout?: number;
  loading?: boolean;
  retry?: boolean;
}

export function createAsyncComponent<
  T extends Component = {
    new (): ComponentPublicInstance;
  },
>(loader: AsyncComponentLoader<T>, options: Options = {}) {
  const { size = 'small', delay = 100, timeout = 30000, loading = false, retry = true } = options;
  return defineAsyncComponent({
    loader,
    loadingComponent: loading ? <Spin spinning={true} size={size} /> : undefined,
    // The error component will be displayed if a timeout is
    // provided and exceeded. Default: Infinity.
    // TODO
    timeout,
    // errorComponent
    // Defining if component is suspensible. Default: true.
    // suspensible: false,
    delay,
    /**
     *
     * @param {*} error Error message object
     * @param {*} retry A function that indicating whether the async component should retry when the loader promise rejects
     * @param {*} fail  End of failure
     * @param {*} attempts Maximum allowed retries number
     */
    onError: !retry
      ? noop
      : (error, retry, fail, attempts) => {
          console.warn(`Async component load failed (attempt ${attempts}):`, error);
          if (error.message.match(/fetch/) && attempts <= 3) {
            // retry on fetch errors, 3 max attempts
            retry();
          } else {
            // Note that retry/fail are like resolve/reject of a promise:
            // one of them must be called for the error handling to continue.
            fail();
          }
        },
  });
}

// export const BasePageModal = createLazyAsyncComponent(() => import('./BasePageModal.vue'));
export function createLazyAsyncComponent<T extends Component = { new (): ComponentPublicInstance }>(
  loader: AsyncComponentLoader<T>,
) {
  const cachedResult = shallowRef<{ default: T } | T | null>(null);
  const loading = shallowRef(false);

  return defineComponent({
    name: 'LazyAsyncComponent',
    setup(_, { slots, attrs }) {
      const load = async () => {
        if (cachedResult.value || loading.value) return;

        loading.value = true;
        try {
          const module = await loader();
          cachedResult.value = module;
        } catch (error) {
          console.error('Load failed:', error);
        } finally {
          loading.value = false;
        }
      };

      onMounted(load);

      return () => {
        if (cachedResult.value) {
          const component = cachedResult.value.default || cachedResult.value;
          return h(component, { ...attrs }, slots);
        }

        if (slots.default) return slots.default();

        return null;
      };
    },
  }) as T;
}

export function onMountedAndActivated(hook: Function, target?: ComponentInternalInstance | null) {
  onMounted(hook, target);
  onActivated(hook, target);
}
